import {SearchIcon, XCircleFillIcon} from '@primer/octicons-react'
import {TextInput, useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import {useId, useRef} from 'react'
import type {TextInputProps} from '@primer/react'
import styles from './Search.module.css'

export interface SearchProps extends Pick<TextInputProps, 'placeholder' | 'className' | 'aria-describedby'> {
  /** Accessible name for the search input. */
  'aria-label': string
  /** Current query. The search field is always controlled. */
  value: string
  /** Called with the new query on every edit, and with an empty string when cleared. */
  onChange: (value: string) => void
  /** Called before `onChange('')` when the Clear button is activated. */
  onClear?: () => void
  /** Accessible name for the Clear button. Defaults to "Clear search". */
  clearLabel?: string
  /** Ref to the underlying input, for example to announce results from it. */
  ref?: React.Ref<HTMLInputElement>
  /** A related filter or sort control rendered after the input. */
  targetedAction?: React.ReactNode
}

const Search = ({
  value,
  onChange,
  onClear,
  clearLabel = 'Clear search',
  ref,
  targetedAction,
  ...props
}: SearchProps) => {
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const mergedRef = useMergedRefs(inputRef, ref)
  return (
    <div className={styles.sidebarSearchWrapper} data-component="Sidebar.Search">
      <TextInput
        {...props}
        className={clsx(styles.sidebarSearch, props.className)}
        id={id}
        ref={mergedRef}
        block
        leadingVisual={SearchIcon}
        size="small"
        type="search"
        value={value}
        onChange={event => onChange(event.target.value)}
        trailingAction={
          value.length > 0 ? (
            <TextInput.Action
              type="button"
              icon={XCircleFillIcon}
              aria-label={clearLabel}
              onClick={() => {
                onClear?.()
                onChange('')
                // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-input-handler -- Restore focus as Clear disappears; deferring could override the user's next focus move.
                inputRef.current?.focus()
              }}
            />
          ) : undefined
        }
      />
      {targetedAction}
    </div>
  )
}

type EmptySearchResultsItemProps = React.HTMLAttributes<HTMLElement> & {
  /** Use `div` inside `Sidebar.Section`. The `li` default is for list-based navigation. */
  as?: 'li' | 'div'
  /** Message shown when no items match the search. */
  children: React.ReactNode
}

const EmptySearchResultsItem = ({as: Component = 'li', children, className, ...props}: EmptySearchResultsItemProps) => (
  <Component
    {...props}
    className={clsx(styles.emptySearchResultsItem, className)}
    data-component="Sidebar.Search.EmptySearchResultsItem"
  >
    {children}
  </Component>
)

Search.EmptySearchResultsItem = EmptySearchResultsItem

export default Search
